import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { segmentDirections } from '@constants/component-configs';
import {
  detailEditCircleStyle,
  detailActiveCircleStyle,
  detailEditLineStyle,
  detailActiveLineStyle,
  detailPolyLineStyle
} from '@constants/data-detail';
import {
  getOneDirectionIcon,
  getBothDirectionIcon
} from '@icons/map-icons';
import CircleMarker from '@shared/circle-marker';
import MapFigure from '@shared/map-figure';
import Polyline from '@shared/polyline';
import { generatePath } from '@utils/map-utils';

const lineInteractions = {
  clickable: false,
  draggable: false,
  editable: false
};

const MapEditFigure = ({
  direction,
  end: endProp,
  geometryLocked: geometryLockedProp,
  onChange,
  shape,
  start: startProp
}) => {
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState(startProp);
  const [end, setEnd] = useState(endProp);
  const [geometryLocked] = useState(geometryLockedProp);

  // Sync state when props change
  useEffect(() => {
    if (startProp !== start && !dragging) {
      setStart(startProp);
    }
  }, [startProp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (endProp !== end && !dragging) {
      setEnd(endProp);
    }
  }, [endProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Memoized styles
  const draggableStyle = useMemo(() => ({
    ...detailEditCircleStyle,
    draggable: true
  }), []);

  const endDraggableStyle = useMemo(() => ({
    ...draggableStyle,
    zIndex: draggableStyle.zIndex * 2
  }), [draggableStyle]);

  const lineStyle = useMemo(() => ({
    ...detailEditLineStyle,
    ...lineInteractions
  }), []);

  const centeredElementStyle = useMemo(() => ({
    ...detailActiveLineStyle,
    ...detailActiveCircleStyle,
    ...lineInteractions,
    ...detailPolyLineStyle
  }), []);

  const handleStartChange = useCallback(({ latLng }) => {
    setStart(latLng.toJSON());
    setDragging(true);
  }, []);

  const handleEndChange = useCallback(({ latLng }) => {
    setEnd(latLng.toJSON());
    setDragging(true);
  }, []);

  const handleStartChanged = useCallback(event => {
    const newStart = event.latLng.toJSON();
    setStart(newStart);
    setDragging(false);
    onChange({ start: newStart, end, geometryLocked, dragging: false });
  }, [end, geometryLocked, onChange]);

  const handleEndChanged = useCallback(event => {
    const newEnd = event.latLng.toJSON();
    setEnd(newEnd);
    setDragging(false);
    onChange({ start, end: newEnd, geometryLocked, dragging: false });
  }, [start, geometryLocked, onChange]);

  const getSelectedLineOptions = useCallback(() => {
    const options = lineStyle;
    if (direction && !dragging) {
      if (direction === segmentDirections.one.value) {
        return {
          ...options,
          icons: [{
            ...options.icons[0],
            icon: getOneDirectionIcon()
          }]
        };
      }
      if (direction === segmentDirections.both.value) {
        return {
          ...options,
          icons: [{
            ...options.icons[0],
            icon: getBothDirectionIcon()
          }]
        };
      }
    }
    return options;
  }, [direction, dragging, lineStyle]);

  const getPolylinePath = useCallback(() => {
    if (shape && shape.coordinates) {
      return generatePath(shape.coordinates);
    }
    return [start, end];
  }, [shape, start, end]);

  const selectedLineOptions = getSelectedLineOptions();

  return (
    <div>
      {shape &&
        <MapFigure shape={shape} options={centeredElementStyle} />
      }
      {start && !geometryLocked &&
        <AdvancedMarker
          anchorLeft="-50%"
          anchorTop="-50%"
          draggable
          onDrag={handleStartChange}
          onDragEnd={handleStartChanged}
          position={start}
          zIndex={draggableStyle.zIndex}
        >
          <CircleMarker style={draggableStyle.icon} />
        </AdvancedMarker>
      }
      {end && !geometryLocked &&
        <AdvancedMarker
          anchorLeft="-50%"
          anchorTop="-50%"
          draggable
          onDrag={handleEndChange}
          onDragEnd={handleEndChanged}
          position={end}
          zIndex={endDraggableStyle.zIndex}
        >
          <CircleMarker style={endDraggableStyle.icon} />
        </AdvancedMarker>
      }
      {start && end &&
        <Polyline
          path={getPolylinePath()}
          options={selectedLineOptions}
        />
      }
    </div>
  );
};

MapEditFigure.propTypes = {
  direction: PropTypes.number,
  end: PropTypes.object,
  geometryLocked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  shape: PropTypes.object,
  start: PropTypes.object
};

export default MapEditFigure;
